import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, fmtError, fmtDate } from "../api";
import { catalogQuery } from "../rollenmodellApi";
import { Smartphone, Wrench, BarChart3, Settings, ChevronLeft, HelpCircle, Check } from "lucide-react";

const ICONS = { smartphone: Smartphone, wrench: Wrench, "bar-chart": BarChart3, settings: Settings };

export default function RollenDetail() {
  const { role } = useParams();
  const navigate = useNavigate();
  const { data: catalog, isLoading, isError } = useQuery({ queryKey: ["rollenmodell-catalog"], queryFn: catalogQuery });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState({});

  useEffect(() => {
    if (!catalog) return;
    const r = (catalog.roles || []).find((x) => x.key === role);
    if (!r) { setError("Unbekannte Rolle."); setData(null); }
    else { setError(""); setData(r); }
  }, [catalog, role]);

  const patchLocal = (storyId, itemId, patch) => {
    setData((prev) => ({
      ...prev,
      stories: prev.stories.map((s) => s.id !== storyId ? s : {
        ...s, checklist: s.checklist.map((c) => c.id === itemId ? { ...c, ...patch } : c),
      }),
    }));
  };

  const save = async (storyId, item, patch) => {
    setSaving((s) => ({ ...s, [item.id]: true }));
    try {
      const { data: res } = await api.post("/rollenmodell/check", { item_id: item.id, ...patch });
      patchLocal(storyId, item.id, { updated_at: res.updated_at });
    } catch (e) {
      setError(fmtError(e, "Speichern fehlgeschlagen"));
    } finally {
      setSaving((s) => { const n = { ...s }; delete n[item.id]; return n; });
    }
  };

  const toggle = (storyId, item) => {
    const next = !item.checked;
    patchLocal(storyId, item.id, { checked: next });
    save(storyId, item, { checked: next });
  };

  if ((isError || error) && !data) {
    return (
      <div className="container-wd py-8" data-testid="rollen-detail-page">
        <button className="btn-secondary" data-testid="rollen-detail-back" onClick={() => navigate("/start")}>
          <ChevronLeft size={16} /> Zurück
        </button>
        <div className="card-wd p-5 mt-4" style={{ borderColor: "#DC2626" }}>
          <p className="text-sm font-semibold m-0" style={{ color: "#DC2626" }}>{error || "Rolle konnte nicht geladen werden."}</p>
        </div>
      </div>
    );
  }

  if (isLoading || !data) return <div className="container-wd py-8"><p className="text-sm" style={{ color: "#64748B" }} data-testid="rollen-detail-loading">Laden…</p></div>;

  const Icon = ICONS[data.icon] || Wrench;
  const { total, done } = data.stories.reduce(
    (a, s) => ({ total: a.total + s.checklist.length, done: a.done + s.checklist.filter((c) => c.checked).length }),
    { total: 0, done: 0 },
  );

  return (
    <div className="container-wd py-8" data-testid="rollen-detail-page">
      <button className="btn-secondary" data-testid="rollen-detail-back" onClick={() => navigate("/start")}>
        <ChevronLeft size={16} /> Zurück zur Rollenübersicht
      </button>

      <div className="card-wd p-0 mt-4 overflow-hidden" style={{ border: `1px solid ${data.border}` }}>
        <div className="flex items-center gap-3 px-6 py-5" style={{ background: data.bg, borderBottom: `1px solid ${data.border}` }}>
          <Icon size={26} color={data.color} />
          <div>
            <h1 className="text-2xl font-extrabold m-0" style={{ color: data.color }} data-testid="rollen-detail-title">{data.label}</h1>
            <p className="text-sm m-0" style={{ color: "#64748B" }}>{data.summary}</p>
          </div>
          <span className="ml-auto text-sm font-bold" data-testid="rollen-detail-progress" style={{ color: data.color }}>
            Abnahme {done}/{total}
          </span>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-5">
        {data.stories.map((s, idx) => (
          <div key={s.id} className="card-wd p-5" data-testid={`rollen-story-${s.id}`}>
            <div className="flex items-start gap-3">
              <span className="rounded-lg flex items-center justify-center font-extrabold text-sm shrink-0"
                style={{ width: 30, height: 30, background: data.bg, color: data.color, border: `1px solid ${data.border}` }}>{idx + 1}</span>
              <h2 className="text-base md:text-lg font-bold m-0">{s.title}</h2>
            </div>

            {s.prueffragen?.length > 0 && (
              <div className="mt-4 pl-1">
                <p className="text-xs font-extrabold uppercase mb-2" style={{ color: "#94A3B8", letterSpacing: ".04em" }}>Prüffragen</p>
                <ul className="flex flex-col gap-2 m-0 p-0" style={{ listStyle: "none" }}>
                  {s.prueffragen.map((q, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "#475569" }} data-testid={`rollen-pruef-${s.id}-${i}`}>
                      <HelpCircle size={16} className="shrink-0 mt-0.5" style={{ color: data.color }} />
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4 pl-1">
              <p className="text-xs font-extrabold uppercase mb-2" style={{ color: "#94A3B8", letterSpacing: ".04em" }}>Abnahme-Checkliste</p>
              <div className="flex flex-col gap-3">
                {s.checklist.map((c) => (
                  <div key={c.id} data-testid={`rollen-check-${c.id}`} className="rounded-xl p-3"
                    style={{ background: c.checked ? data.bg : "#F8FAFC", border: `1px solid ${c.checked ? data.border : "#E2E8F0"}` }}>
                    <button type="button" data-testid={`rollen-check-btn-${c.id}`} onClick={() => toggle(s.id, c)}
                      disabled={!!saving[c.id]}
                      className="flex items-center gap-3 w-full text-left"
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                      <span className="rounded-md flex items-center justify-center shrink-0"
                        style={{ width: 22, height: 22, background: c.checked ? data.color : "#fff", border: `1px solid ${c.checked ? data.color : "#CBD5E1"}` }}>
                        {c.checked && <Check size={15} color="#fff" strokeWidth={3} />}
                      </span>
                      <span className="text-sm font-medium" style={{ color: "#0F172A", textDecoration: c.checked ? "line-through" : "none", opacity: c.checked ? 0.7 : 1 }}>{c.text}</span>
                    </button>

                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-2 mt-3" style={{ paddingLeft: 34 }}>
                      <input
                        data-testid={`rollen-note-${c.id}`}
                        className="input-wd text-sm"
                        placeholder="Notiz zur Abnahme (optional)"
                        value={c.note || ""}
                        onChange={(e) => patchLocal(s.id, c.id, { note: e.target.value })}
                        onBlur={(e) => save(s.id, c, { note: e.target.value })}
                      />
                      <input
                        data-testid={`rollen-pruefer-${c.id}`}
                        className="input-wd text-sm"
                        placeholder="Prüfer (Name)"
                        value={c.checked_by || ""}
                        onChange={(e) => patchLocal(s.id, c.id, { checked_by: e.target.value })}
                        onBlur={(e) => save(s.id, c, { checked_by: e.target.value })}
                      />
                    </div>
                    {c.updated_at && (
                      <p className="text-xs mt-2 mb-0" style={{ color: "#94A3B8", paddingLeft: 34 }} data-testid={`rollen-updated-${c.id}`}>
                        zuletzt aktualisiert: {fmtDate(c.updated_at)}{saving[c.id] ? " · speichert…" : ""}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
